import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OsScroll from '../ui/OsScroll';
import EventChip from './EventChip';
import { DEFAULT_EVENT_COLOR } from '../../extensions/bundled/calendar/calendarStore';
import { appDateLocale } from '../../utils/appLocale';
import {
  MINUTES_PER_DAY,
  isSameDay,
  minutesOfDay,
  layoutDayEvents,
  eventsInRange,
  eventRange,
  startOfDay,
  addDays,
  dayAtMinutes,
  floorMinutes,
  snapMinutes,
  formatTime,
  formatDayLong,
  toDateKey,
} from '../../utils/calendarDates';

export const HOUR_HEIGHT = 48; // px — 30-min slots at 24px
export const PX_PER_MIN = HOUR_HEIGHT / 60;
export const SLOT_MINUTES = 30;
const MOVE_SNAP_MINUTES = 15;
const AUTO_SCROLL_HOUR = 8;
const CLICK_DRAG_THRESHOLD_PX = 3;

function minutesFromClientY(clientY, bodyRect) {
  const raw = (clientY - bodyRect.top) / PX_PER_MIN;
  return Math.min(Math.max(raw, 0), MINUTES_PER_DAY);
}

function dayIndexFromClientX(clientX, bodyRect, dayCount) {
  const colWidth = bodyRect.width / dayCount;
  const idx = Math.floor((clientX - bodyRect.left) / colWidth);
  return Math.min(Math.max(idx, 0), dayCount - 1);
}

/**
 * The shared Week/Day time grid (PLAN §3.5): 30-minute slots, all-day row,
 * click-drag on empty grid creates a draft event, drag-move + bottom-edge
 * resize on existing events, red current-time indicator, auto-scroll to
 * 08:00. Drag is hand-rolled pointer math over the column container's
 * bounding rect — dnd-kit is list-oriented and a time grid needs pixel↔minute
 * conversion, so mouse events + window listeners it is.
 */
export default function TimeGrid({
  draftOpen = false,
  days,
  events,
  timeFormat,
  now: nowProp = null,
  onSelectEvent,
  onEventContextMenu,
  onCreateDraft,
  onUpdateEventTimes,
}) {
  const { t } = useTranslation();
  const colsRef = useRef(null);
  const osRef = useRef(null);
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // The slot you dragged stays lit while the editor is open. Without this the
  // ghost is torn down by `setDrag(null)` on mouse-up and the modal appears
  // over a grid with nothing marked, so you cannot see which slot you are
  // filling in (owner, 01.09.2026).
  const [pendingCreate, setPendingCreate] = useState(null); // render mirror of dragRef.current
  const [internalNow, setInternalNow] = useState(() => new Date());
  const now = nowProp || internalNow;

  // Current-time indicator ticks once a minute (only when not injected).
  useEffect(() => {
    if (nowProp) return undefined;
    const id = setInterval(() => setInternalNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [nowProp]);

  // Auto-scroll the grid to 08:00 on mount (OsScroll's viewport element; rAF
  // retry covers OverlayScrollbars' deferred init — DbDatePicker pattern).
  useEffect(() => {
    let raf;
    let tries = 0;
    const scroll = () => {
      const viewport = osRef.current?.osInstance?.()?.elements?.().viewport;
      if (viewport) {
        viewport.scrollTop = AUTO_SCROLL_HOUR * HOUR_HEIGHT;
      } else if (tries++ < 10) {
        raf = requestAnimationFrame(scroll);
      }
    };
    raf = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(raf);
  }, []);

  const rangeStart = startOfDay(days[0]);
  const rangeEnd = addDays(startOfDay(days[days.length - 1]), 1);
  const visible = useMemo(
    () => eventsInRange(events, rangeStart, rangeEnd),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, rangeStart.getTime(), rangeEnd.getTime()]
  );
  const allDayEvents = visible.filter((e) => e.all_day);
  const timedByDay = useMemo(
    () => days.map((day) => layoutDayEvents(visible, day)),
    [days, visible]
  );

  /* ── drag machinery ─────────────────────────────────────────────────────── */

  const beginDrag = (state, e) => {
    const bodyRect = colsRef.current?.getBoundingClientRect();
    if (!bodyRect) return;
    const next = {
      ...state,
      bodyRect,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moved: false,
    };
    dragRef.current = next;
    setDrag(next);

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dist =
        Math.abs(ev.clientX - d.startClientX) + Math.abs(ev.clientY - d.startClientY);
      if (dist > CLICK_DRAG_THRESHOLD_PX) d.moved = true;
      const minutes = minutesFromClientY(ev.clientY, d.bodyRect);
      const dayIndex = dayIndexFromClientX(ev.clientX, d.bodyRect, days.length);
      let ghost = d.ghost;
      if (d.type === 'create') {
        const a = d.anchorMinutes;
        const b = snapMinutes(minutes, SLOT_MINUTES);
        ghost = {
          dayIndex: d.dayIndex,
          startMin: Math.min(a, b),
          endMin: Math.max(Math.max(a, b), Math.min(a, b) + SLOT_MINUTES),
        };
      } else if (d.type === 'move') {
        const duration = d.ghost.endMin - d.ghost.startMin;
        let startMin = snapMinutes(minutes - d.grabOffsetMinutes, MOVE_SNAP_MINUTES);
        startMin = Math.min(Math.max(startMin, 0), MINUTES_PER_DAY - duration);
        ghost = { dayIndex, startMin, endMin: startMin + duration };
      } else if (d.type === 'resize') {
        const endMin = Math.max(
          snapMinutes(minutes, MOVE_SNAP_MINUTES),
          d.ghost.startMin + MOVE_SNAP_MINUTES
        );
        ghost = { ...d.ghost, endMin };
      }
      dragRef.current = { ...d, ghost };
      setDrag(dragRef.current);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d) return;
      const { ghost } = d;
      if (d.type === 'create') {
        setPendingCreate(ghost);
        onCreateDraft?.({
          start: dayAtMinutes(days[ghost.dayIndex], ghost.startMin),
          end: dayAtMinutes(days[ghost.dayIndex], ghost.endMin),
        });
        return;
      }
      if (!d.moved) {
        // A plain click on an event opens the popover, it doesn't "move" it.
        if (d.type === 'move') onSelectEvent?.(d.event, d.chipRect);
        return;
      }
      onUpdateEventTimes?.(d.event, {
        start: dayAtMinutes(days[ghost.dayIndex], ghost.startMin),
        end: dayAtMinutes(days[ghost.dayIndex], ghost.endMin),
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleColumnMouseDown = (dayIndex) => (e) => {
    if (e.button !== 0) return;
    const bodyRect = colsRef.current?.getBoundingClientRect();
    if (!bodyRect) return;
    const anchor = floorMinutes(minutesFromClientY(e.clientY, bodyRect), SLOT_MINUTES);
    beginDrag(
      {
        type: 'create',
        dayIndex,
        anchorMinutes: anchor,
        ghost: { dayIndex, startMin: anchor, endMin: anchor + SLOT_MINUTES },
      },
      e
    );
  };

  const handleEventMouseDown = (event, dayIndex, seg) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const bodyRect = colsRef.current?.getBoundingClientRect();
    if (!bodyRect) return;
    const grabMinutes = minutesFromClientY(e.clientY, bodyRect);
    beginDrag(
      {
        type: 'move',
        event,
        dayIndex,
        grabOffsetMinutes: grabMinutes - seg.topMinutes,
        chipRect: e.currentTarget.getBoundingClientRect(),
        ghost: { dayIndex, startMin: seg.topMinutes, endMin: seg.topMinutes + seg.durationMinutes },
      },
      e
    );
  };

  const handleResizeMouseDown = (event, dayIndex, seg) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    beginDrag(
      {
        type: 'resize',
        event,
        dayIndex,
        ghost: { dayIndex, startMin: seg.topMinutes, endMin: seg.topMinutes + seg.durationMinutes },
      },
      e
    );
  };

  /* ── render ─────────────────────────────────────────────────────────────── */

  const hourLines = Array.from({ length: 48 }, (_, i) => i); // 30-min lines
  const showHeader = days.length > 1;
  // Cleared when the editor closes — saved, cancelled or dismissed all end the
  // same way, so this needs no knowledge of which.
  useEffect(() => {
    if (!draftOpen) setPendingCreate(null);
  }, [draftOpen]);

  // The live drag wins while dragging; the pending slot takes over once the
  // mouse is up and the editor has it.
  const ghost = drag?.moved || drag?.type === 'create' ? drag.ghost : pendingCreate;

  return (
    <div className="cal-timegrid" role="grid" aria-label={t(days.length > 1 ? 'calendar.view.week' : 'calendar.view.day')}>
      {showHeader && (
        <div className="cal-timegrid-header" role="row">
          <div className="cal-timegrid-gutter" aria-hidden="true" />
          {days.map((day) => (
            <div
              key={toDateKey(day)}
              role="columnheader"
              className={`cal-timegrid-headercell${isSameDay(day, now) ? ' cal-timegrid-headercell--today' : ''}`}
            >
              {day.toLocaleDateString(appDateLocale(), { weekday: 'short', day: 'numeric' })}
            </div>
          ))}
        </div>
      )}

      <div className="cal-timegrid-allday" role="row">
        <div className="cal-timegrid-allday-label">{t('calendar.allDay')}</div>
        <div className="cal-timegrid-allday-cols">
          {days.map((day) => (
            <div key={toDateKey(day)} className="cal-timegrid-allday-col" role="gridcell">
              {allDayEvents
                .filter((e) => {
                  const { start, end } = eventRange(e);
                  const ds = startOfDay(day);
                  return start < addDays(ds, 1) && end > ds;
                })
                .map((event) => (
                  <EventChip
                    key={event.uid}
                    event={event}
                    timeFormat={timeFormat}
                    showTime={false}
                    onSelect={onSelectEvent}
                    onEventContextMenu={onEventContextMenu}
                  />
                ))}
            </div>
          ))}
        </div>
      </div>

      <OsScroll ref={osRef} className="cal-timegrid-scroll">
        <div className="cal-timegrid-body" style={{ height: 24 * HOUR_HEIGHT }}>
          <div className="cal-timegrid-gutter" aria-hidden="true" style={{ position: 'relative' }}>
            {Array.from({ length: 23 }, (_, i) => i + 1).map((hour) => (
              <span
                key={hour}
                className="cal-timegrid-hourlabel"
                style={{ position: 'absolute', top: hour * HOUR_HEIGHT, right: 8 }}
              >
                {formatTime(dayAtMinutes(days[0], hour * 60), timeFormat)}
              </span>
            ))}
          </div>
          <div className="cal-timegrid-cols" ref={colsRef} data-testid="cal-timegrid-cols">
            {days.map((day, dayIndex) => {
              const isToday = isSameDay(day, now);
              const segs = timedByDay[dayIndex];
              return (
                <div
                  key={toDateKey(day)}
                  role="gridcell"
                  aria-label={formatDayLong(day)}
                  className="cal-timegrid-col"
                  data-testid={`cal-day-col-${dayIndex}`}
                  onMouseDown={handleColumnMouseDown(dayIndex)}
                >
                  {hourLines.map((i) => (
                    <div
                      key={i}
                      className={`cal-timegrid-slotline${i % 2 === 0 ? ' cal-timegrid-slotline--hour' : ''}`}
                      style={{ top: i * (HOUR_HEIGHT / 2) }}
                    />
                  ))}

                  {segs.map(({ event, topMinutes, durationMinutes, column, columns }) => {
                    const isDragTarget =
                      drag && drag.type !== 'create' && drag.event?.uid === event.uid && drag.moved;
                    const color = event.color || DEFAULT_EVENT_COLOR;
                    const widthPct = 100 / columns;
                    const { start, end } = eventRange(event);
                    return (
                      <div
                        key={event.uid}
                        role="button"
                        tabIndex={0}
                        aria-label={t('calendar.timegrid.eventAria', {
                          title: event.title || t('calendar.noTitle'),
                          start: formatTime(start, timeFormat),
                          end: formatTime(end, timeFormat),
                        })}
                        className={`cal-timegrid-event${isDragTarget ? ' cal-timegrid-event--dragging' : ''}${event.pending_invite ? ' cal-timegrid-event--pending' : ''}`}
                        data-testid={`cal-event-${event.uid}`}
                        style={{
                          top: topMinutes * PX_PER_MIN,
                          height: Math.max(durationMinutes * PX_PER_MIN, 14),
                          left: `calc(${column * widthPct}% + 2px)`,
                          width: `calc(${widthPct}% - 5px)`,
                          borderLeftColor: color,
                          background: `${color}2b`,
                        }}
                        onMouseDown={handleEventMouseDown(event, dayIndex, {
                          topMinutes,
                          durationMinutes,
                        })}
                        // Right-click → event context menu; suppressed default
                        // only on the event surface (fix round 4).
                        onContextMenu={(e) => {
                          if (!onEventContextMenu) return;
                          e.preventDefault();
                          e.stopPropagation();
                          onEventContextMenu(event, { x: e.clientX, y: e.clientY });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectEvent?.(event, e.currentTarget.getBoundingClientRect());
                          }
                        }}
                      >
                        <div className="cal-timegrid-event-title">
                          {event.title || t('calendar.noTitle')}
                          {event.pending_invite && (
                            <span className="cal-chip-badge">{t('calendar.chip.pending')}</span>
                          )}
                        </div>
                        {durationMinutes >= 40 && (
                          <div className="cal-timegrid-event-time">
                            {formatTime(start, timeFormat)} – {formatTime(end, timeFormat)}
                          </div>
                        )}
                        <div
                          className="cal-timegrid-resize"
                          data-testid={`cal-event-resize-${event.uid}`}
                          onMouseDown={handleResizeMouseDown(event, dayIndex, {
                            topMinutes,
                            durationMinutes,
                          })}
                        />
                      </div>
                    );
                  })}

                  {ghost && ghost.dayIndex === dayIndex && (
                    <div
                      className="cal-timegrid-draft"
                      data-testid="cal-drag-ghost"
                      style={{
                        top: ghost.startMin * PX_PER_MIN,
                        height: Math.max((ghost.endMin - ghost.startMin) * PX_PER_MIN, 12),
                      }}
                    >
                      {formatTime(dayAtMinutes(day, ghost.startMin), timeFormat)} –{' '}
                      {formatTime(dayAtMinutes(day, ghost.endMin), timeFormat)}
                    </div>
                  )}

                  {isToday && (
                    <div
                      className="cal-timegrid-nowline"
                      data-testid="cal-now-line"
                      style={{ top: minutesOfDay(now) * PX_PER_MIN }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </OsScroll>
    </div>
  );
}
